/** biome-ignore-all lint/suspicious/noArrayIndexKey: because we can't rely on highlights for ids */
import { Color } from "@brookbush/database";
import type { Highlight } from "@brookbush/types";

import clsx from "clsx";
import { useMemo } from "react";
import { flushSync } from "react-dom";

import Mounted from "../Mounted";
import { getRangeFromOffsets } from "~/utils/helpers";

interface Props {
	parentRef: React.RefObject<HTMLDivElement | null>;
	highlights: Highlight[];
	setActiveHighlight: (val: Highlight | null) => void;
	activeHighlight: Highlight | null;
	setRange: (val: Range | null) => void;
}

const AnnotationsProviderHighlights = ({
	parentRef,
	highlights,
	setActiveHighlight,
	setRange,
}: Props) => {
	const items = useMemo(
		() =>
			parentRef.current &&
			highlights

				.map((highlight) => {
					const range = getRangeFromOffsets(
						parentRef.current!,
						highlight.startOffset,
						highlight.endOffset,
					);

					return { highlight, range };
				})
				.filter((item) => !item.range?.collapsed)
				.flatMap((item) =>
					Array.from(item.range?.getClientRects() ?? []).map((subItem) => ({
						rect: subItem,
						...item,
					})),
				),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[highlights, parentRef],
	);

	if (!items) return;

	return (
		<Mounted>
			{items.map((item, i) => (
				<>
					<div
						key={i}
						className={clsx("absolute rounded opacity-50", {
							"bg-red-300 shadow dark:bg-red-300/50":
								item.highlight.color === Color.RED,
							"bg-amber-300 dark:bg-amber-300/50":
								item.highlight.color === Color.YELLOW,
							"bg-green-300 dark:bg-green-300/50":
								item.highlight.color === Color.GREEN,
						})}
						style={{
							top:
								item.rect?.top! -
								parentRef.current?.getBoundingClientRect().top! -
								3,
							left:
								item.rect!.left -
								parentRef.current?.getBoundingClientRect().left!,
							width: item.rect.width,
							height: item.rect.height + 6,
						}}
					/>

					<button
						key={`${i}-btn`}
						className={clsx("z-10")}
						style={{
							position: "absolute",
							top:
								item.rect?.top! -
								parentRef.current?.getBoundingClientRect().top! -
								3,
							left:
								item.rect!.left -
								parentRef.current?.getBoundingClientRect().left!,
							width: item.rect.width,
							height: item.rect.height + 6,
						}}
						onClick={(e) => {
							e.stopPropagation();
							flushSync(() => setActiveHighlight(item.highlight));
							setRange(item.range);
						}}
					></button>
				</>
			))}
		</Mounted>
	);
};

export default AnnotationsProviderHighlights;
